import { Landmark, CreditCard, Wallet, BadgePercent, CheckCircle2 } from "lucide-react";
import { Seo, breadcrumbSchema } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TrustStrip } from "../components/TrustStrip";
import { CtaBlock } from "../components/CtaBlock";
import { Reveal, Eyebrow } from "../components/Reveal";

const CARRIERS = ["State Farm", "GEICO", "USAA", "Fred Loya Insurance", "Allstate", "Progressive", "Liberty Mutual", "Nationwide"];

const BLOCKS = [
  {
    icon: CreditCard,
    title: "Payment Methods Accepted",
    body: "We keep payment simple at pickup, whether your repair is insurance-covered, out-of-pocket, or a blend of both.",
    placeholder: "[PLACEHOLDER: payment methods — e.g. Visa, Mastercard, Amex, Discover, cash, cashier's check, financing, Zelle, Apple Pay]",
  },
  {
    icon: Wallet,
    title: "Financing Options",
    body: "Repair financing is available for deductibles, out-of-pocket repairs, and upgrades — quick application, fast decisions, plain-language terms.",
    placeholder: "[PLACEHOLDER: financing provider name, APR ranges, promotional terms, minimum amounts]",
  },
  {
    icon: BadgePercent,
    title: "Deductible Assistance",
    body: "Ask our advisors what options your policy allows before you assume the deductible is out of pocket.",
    placeholder: "[PLACEHOLDER: deductible assistance policy / programs offered]",
  },
];

export default function WhatWeAccept() {
  return (
    <div data-testid="what-we-accept-page">
      <Seo
        title="Insurance & Payment Options in Carrollton, TX | Westway"
        description="State Farm, GEICO, USAA, Progressive & more accepted — plus flexible payment and financing at Westway Collision Center, Carrollton, TX."
        path="/what-we-accept"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "We Accept", path: "/what-we-accept" }])]}
      />

      <section className="relative pt-32 lg:pt-40 pb-14 overflow-hidden">
        <div className="absolute inset-0 surface-weave opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "We Accept", path: "/what-we-accept" }]} />
          <Reveal>
            <Eyebrow>Zero Friction</Eyebrow>
            <h1 className="mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.06] text-balance max-w-4xl">
              Insurance &amp; Payment Options in Carrollton, TX
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
              Westway Collision Center is an insurance-approved DRP partner working with all major
              carriers — plus flexible payment and financing options — so cost never forces a
              compromise on how your vehicle is repaired.
            </p>
          </Reveal>
        </div>
      </section>

      <TrustStrip />

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full rounded-2xl border border-accent/25 bg-gradient-to-br from-accent/[0.07] via-surface to-surface p-7 sm:p-9">
              <Landmark size={26} className="text-accent" strokeWidth={1.6} aria-hidden="true" />
              <h2 className="mt-4 font-display text-xl sm:text-2xl font-semibold text-slate-100">Insurance Carriers We Work With</h2>
              <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed">
                We are an insurance-approved DRP partner and manage your claim end-to-end — estimates,
                supplements, adjuster communication, and rental coordination. Texas law guarantees
                your right to choose your own shop, so your carrier works with us on your terms.
              </p>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="carrier-list">
                {CARRIERS.map((c) => (
                  <li key={c} className="flex items-center gap-2.5 text-sm text-slate-200">
                    <CheckCircle2 size={15} className="text-accent shrink-0" aria-hidden="true" />
                    {c}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                Plus most other carriers writing auto policies in Texas — if yours isn&apos;t listed,
                call (214) 888-2886 and we&apos;ll confirm in minutes.
              </p>
            </div>
          </Reveal>
          {BLOCKS.map((b, i) => (
            <Reveal key={b.title} delay={(i + 1) * 0.06}>
              <div className="h-full rounded-2xl border border-white/[0.08] bg-surface p-7 sm:p-9 transition-colors duration-500 hover:border-accent/30">
                <b.icon size={26} className="text-accent" strokeWidth={1.6} aria-hidden="true" />
                <h2 className="mt-4 font-display text-xl sm:text-2xl font-semibold text-slate-100">{b.title}</h2>
                <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed">{b.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CtaBlock heading="Questions About Coverage?" sub="Call us with your claim number — we will tell you exactly what your policy covers before you commit to anything." />
        </div>
      </section>
    </div>
  );
}
